// Katalog içe aktarma × sektör paketleri (DR10): "Hedef paket" seçici (etkin paketler + "— Paket
// yok —") + satır içi "Yeni paket…" (ad + sektör → `createKpiPackage` aktarımdan ÖNCE, sonra seçili).
// Paket seçilmezse aktarım gövdesi eskisiyle birebir (`packageId` anahtarı yazılmaz).

import { useId, useMemo, useState } from "react";
import { SECTOR_CODES, SECTOR_LABELS, type KpiPackage, type SectorCode } from "@mahir/shared";
import { ApiError } from "../../../api/client";
import { createKpiPackage } from "../../../api/kpi";
import { KPI_PACKAGES_COPY, packageErrorText } from "../../../lib/kpi-packages-copy";
import { SearchSelect } from "../../common/SearchSelect";
import { errorBox, footerBtn, helpText, inputStyle, labelStyle } from "../kpi-form-styles";

const C = KPI_PACKAGES_COPY.importTarget;
const SECTOR_OPTIONS = SECTOR_CODES.map((c) => ({ value: c, label: SECTOR_LABELS[c] }));

export interface CatalogImportTargetPackageProps {
  packages: readonly KpiPackage[];
  value: string;
  onChange: (packageId: string) => void;
  /** yeni paket oluşturuldu (üst bileşen listeyi tazeler) */
  onCreated: (pkg: KpiPackage) => void;
  disabled?: boolean | undefined;
}

export function CatalogImportTargetPackage(p: CatalogImportTargetPackageProps): JSX.Element {
  const ids = { select: useId(), name: useId(), sector: useId() };
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [sector, setSector] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const options = useMemo(
    () =>
      p.packages
        .filter((x) => x.status === "active")
        .map((x) => ({
          value: x.id,
          label: x.name,
          hint: KPI_PACKAGES_COPY.page.itemCount(x.itemCount),
        })),
    [p.packages],
  );

  async function create(): Promise<void> {
    if (name.trim() === "" || busy) return;
    setBusy(true);
    setError(null);
    try {
      const pkg = await createKpiPackage({
        name: name.trim(),
        sectorCode: sector === "" ? null : (sector as SectorCode),
      });
      p.onCreated(pkg);
      p.onChange(pkg.id);
      setCreating(false);
      setName("");
      setSector("");
    } catch (e) {
      setError(
        e instanceof ApiError
          ? packageErrorText(e.code, e.message, e.details)
          : packageErrorText("", e instanceof Error ? e.message : undefined),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="catalog-import-target-package" style={{ marginBottom: 10 }}>
      <div className="flex items-end gap-2 flex-wrap">
        <div style={{ display: "grid", gap: 4, flex: "0 1 360px", minWidth: 240 }}>
          <label htmlFor={ids.select} style={labelStyle}>
            {C.label}
          </label>
          <SearchSelect
            id={ids.select}
            ariaLabel={C.label}
            value={p.value}
            options={options}
            onChange={p.onChange}
            emptyLabel={C.none}
            placeholder={C.none}
            showCode={false}
            disabled={(p.disabled ?? false) || creating}
          />
        </div>
        {creating ? null : (
          <button
            type="button"
            style={footerBtn()}
            disabled={p.disabled}
            onClick={() => setCreating(true)}
          >
            {C.newPackage}
          </button>
        )}
      </div>
      <div style={{ ...helpText, marginTop: 4 }}>{C.hint}</div>
      {creating ? (
        <div className="flex items-end gap-2 flex-wrap" style={{ marginTop: 8 }}>
          <div style={{ display: "grid", gap: 4, flex: "0 1 280px", minWidth: 200 }}>
            <label htmlFor={ids.name} style={labelStyle}>
              {C.newName}
            </label>
            <input
              id={ids.name}
              value={name}
              maxLength={200}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void create();
                }
              }}
              style={{ ...inputStyle, minHeight: 44 }}
            />
          </div>
          <div style={{ display: "grid", gap: 4, flex: "0 1 240px", minWidth: 180 }}>
            <span id={ids.sector} style={labelStyle}>
              {C.newSector}
            </span>
            <SearchSelect
              ariaLabel={C.newSector}
              value={sector}
              options={SECTOR_OPTIONS}
              onChange={setSector}
              emptyLabel={KPI_PACKAGES_COPY.identity.noSector}
              placeholder={KPI_PACKAGES_COPY.identity.noSector}
              showCode={false}
            />
          </div>
          <button
            type="button"
            style={footerBtn(true)}
            disabled={busy || name.trim() === ""}
            aria-busy={busy || undefined}
            onClick={() => void create()}
          >
            {busy ? C.creating : C.create}
          </button>
          <button
            type="button"
            style={footerBtn()}
            disabled={busy}
            onClick={() => {
              setCreating(false);
              setError(null);
            }}
          >
            {C.cancel}
          </button>
        </div>
      ) : null}
      {error ? (
        <div role="alert" style={{ ...errorBox, marginTop: 8 }}>
          {error}
        </div>
      ) : null}
    </div>
  );
}
